import './applicantEmploymentStatusPage.scss';

import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/RadioGroup/types/RadioOptionClickEvent';
import { isValidClickEvent } from '../../../components/utils/isValidClickEvent';
import { NO, YES, YesOrNo } from '../../../store/applicantStore/types/YesOrNo';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const {
    computed: { progress },
    next,
    previous,
  } = useWorkflowStore();
  const { setDrawerOpen } = useNavigationPaneStore();

  const [isInitialSelection, setIsInitialSelection] = useState(false);

  const {
    isCurrentlyEmployed,
    isSyncingIncome,
    clearCurrentEmployment,
    setIsCurrentlyEmployed,
  } = useApplicantStore();

  const {
    formState: { isDirty },
    handleSubmit,
    register,
    watch,
  } = useForm({
    defaultValues: {
      isCurrentlyEmployedValue: isCurrentlyEmployed,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (event: RadioOptionClickEvent<YesOrNo>): void => {
    if (!('target' in event && 'value' in event.target)) {
      return;
    }

    const isCurrentlyEmployedValue = event.target.value;

    setIsInitialSelection(
      isEmpty(isCurrentlyEmployed) && isValidClickEvent(event),
    );

    setIsCurrentlyEmployed(isCurrentlyEmployedValue);
  };

  const formSubmit = (values: FieldValues): void => {
    const { isCurrentlyEmployedValue } = values;

    if (isCurrentlyEmployedValue) {
      if (isDirty) {
        clearCurrentEmployment();
      }

      navigate(next());
    }
  };

  useEffect(() => {
    if (isInitialSelection) {
      handleSubmit(formSubmit)();
    }
  }, [isInitialSelection]);

  return (
    <ThemeProvider theme={theme}>
      <Box className="applicant-employment-status-page" sx={theme.pageLayout}>
        <header
          data-testid={locator.pages.applicantEmploymentStatus.header.root}
        >
          <button
            aria-label="Previous step"
            data-testid={
              locator.pages.applicantEmploymentStatus.header.previousStepButton
            }
            type="button"
            onClick={previousStep}
          >
            <KeyboardBackspaceRoundedIcon />
          </button>
          <button
            aria-label="Toggle drawer"
            data-testid={
              locator.pages.applicantEmploymentStatus.header.drawerButton
            }
            type="button"
            onClick={() => {
              setDrawerOpen(true);
            }}
          >
            <MenuRoundedIcon />
          </button>
        </header>

        <div className="progress-container" role="complementary">
          <LinearProgress
            aria-label="Application completed percentage"
            data-testid={locator.pages.applicantEmploymentStatus.progress.root}
            value={progress}
            variant="determinate"
          />
        </div>

        <main>
          <form
            data-testid={locator.pages.applicantEmploymentStatus.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.applicantEmploymentStatus.form.h1}>
                {t('ApplicantEmploymentStatusPage.title')}
              </h1>
              <h2 data-testid={locator.pages.applicantEmploymentStatus.form.h2}>
                {t('ApplicantEmploymentStatusPage.subtitle')}
              </h2>

              <div style={{ marginBottom: '3rem' }}>
                <RadioGroup
                  defaultValue={isCurrentlyEmployed}
                  labelPlacement="below"
                  name="isCurrentlyEmployedValue"
                  options={[
                    {
                      label: t('ApplicantEmploymentStatusPage.yes'),
                      value: YES,
                      testId:
                        locator.pages.applicantEmploymentStatus.form
                          .radioOption1,
                    },
                    {
                      label: t('ApplicantEmploymentStatusPage.no'),
                      value: NO,
                      testId:
                        locator.pages.applicantEmploymentStatus.form
                          .radioOption2,
                    },
                  ]}
                  register={register}
                  testId={
                    locator.pages.applicantEmploymentStatus.form.radioGroupRoot
                  }
                  variant="inline"
                  watch={watch}
                  onClick={radioOptionClicked}
                />
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <LoadingButton
                  color="primary"
                  data-testid={
                    locator.pages.applicantEmploymentStatus.form.submitButton
                  }
                  disabled={isEmpty(isCurrentlyEmployed)}
                  loading={isSyncingIncome}
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('ApplicantEmploymentStatusPage.continue')}
                </LoadingButton>
              </div>
            </div>
            <footer
              data-testid={locator.pages.applicantEmploymentStatus.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplicantEmploymentStatusPage';
